// AddDayNoteModal — create a per-day diary note (note or alert).
//
// On submit: POST /api/v1/collections/diary/notes via useDayNotes(). On success the
// hook invalidates the diary week query so the active panel re-renders
// with the new note.
//
// Server cap is 2000 chars; UI cap is 500 (UI_AUDIT §2.3).

import { useEffect, useState } from 'react';
import axios from 'axios';
import { Modal } from '../../../components/ui/Modal/Modal';
import { Button } from '../../../components/ui/Button/Button';
import { Textarea } from '../../../components/form/Textarea';
import { RadioGroup } from '../../../components/form/RadioGroup';
import { useToast } from '../../../hooks/useToast';
import { useDayNotes } from '../hooks/useDayNotes';
import { cn } from '../../../utils/cn';
import type { DiaryNoteType } from '../../../types/api';
import styles from './DayNoteModal.module.css';

const MAX_NOTE_LENGTH = 500;
const WARN_AT = 450;

export interface AddDayNoteModalProps {
  open: boolean;
  driverId: number;
  weekStart: string;
  date: string; // YYYY-MM-DD
  onClose: () => void;
}

export function AddDayNoteModal({
  open,
  driverId,
  weekStart,
  date,
  onClose,
}: AddDayNoteModalProps): JSX.Element | null {
  const toast = useToast();
  const { addNote } = useDayNotes({ driverId, weekStart });
  const [type, setType] = useState<DiaryNoteType>('note');
  const [text, setText] = useState('');

  // Reset whenever the modal is opened on a different day.
  useEffect(() => {
    if (open) {
      setType('note');
      setText('');
    }
  }, [open, date]);

  if (!open) return null;

  const length = text.length;
  const overLimit = length > MAX_NOTE_LENGTH;
  const empty = text.trim().length === 0;
  const counterClass = cn(
    styles.counter,
    !overLimit && length >= WARN_AT && styles.counterWarning,
    overLimit && styles.counterError,
  );

  async function handleSubmit() {
    if (empty || overLimit) return;
    try {
      await addNote.mutateAsync({
        driverId,
        date,
        noteText: text,
        noteType: type,
      });
      toast.success('Note added');
      onClose();
    } catch (err) {
      const message = axios.isAxiosError(err)
        ? (err.response?.data as { message?: string } | undefined)?.message ??
          'Could not add the note.'
        : 'Could not add the note.';
      toast.error(message);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add Day Note"
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={addNote.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={addNote.isPending}
            disabled={empty || overLimit}
          >
            Add Note
          </Button>
        </>
      }
    >
      <div className={styles.body}>
        <RadioGroup
          name="day-note-type"
          label="Type"
          value={type}
          onChange={(v) => setType(v as DiaryNoteType)}
          options={[
            { value: 'note', label: 'Note' },
            { value: 'alert', label: 'Alert' },
          ]}
        />
        <div>
          <Textarea
            label="Note text"
            required
            autoResize
            rows={5}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Add a note for this day…"
            aria-label="Note text"
          />
          <span className={counterClass}>
            {length} / {MAX_NOTE_LENGTH}
          </span>
        </div>
      </div>
    </Modal>
  );
}
